/**
 * Meal Plan Formatting Utilities
 * Functions to clean up and format meal plan data for consistent display
 */

import { LLMMealPlanResultOutgoing, LLMRecipeOutgoing, ShoppingItem } from "../types/mealPlanning";
import { OutgoingSearchResult } from "../types/search";
import { toTitleCase } from "./textFormatting";

/**
 * Recursively formats all name-related properties in meal plan data to title case
 * @param mealPlanData The meal plan data to format
 * @returns A new formatted meal plan data object
 */
export function formatMealPlanNames(mealPlanData: LLMMealPlanResultOutgoing): LLMMealPlanResultOutgoing {
    // Deep clone to avoid mutating the original data
    const formatted = JSON.parse(JSON.stringify(mealPlanData)) as LLMMealPlanResultOutgoing;

    // Format meal plan days
    if (formatted.mealPlanDays) {
        formatted.mealPlanDays = formatted.mealPlanDays.map(day => {
            // Format each meal type (breakfast, lunch, dinner, snack)
            const formattedDay = { ...day };
            
            // Process each meal type
            ['breakfast', 'lunch', 'dinner', 'snack'].forEach(mealType => {
                if (formattedDay[mealType as keyof typeof formattedDay] && 
                    typeof formattedDay[mealType as keyof typeof formattedDay] === 'object' &&
                    'recipes' in (formattedDay[mealType as keyof typeof formattedDay] as any)) {
                    
                    const mealSlot = formattedDay[mealType as keyof typeof formattedDay] as { recipes: LLMRecipeOutgoing[] };
                    mealSlot.recipes = mealSlot.recipes.map(formatRecipe);
                }
            });

            return formattedDay;
        });
    }

    // Format shopping list
    if (formatted.shoppingList) {
        formatted.shoppingList = formatted.shoppingList.map(formatShoppingItem);
    }

    return formatted;
}

/**
 * Formats a recipe's name and all ingredient names
 * @param recipe The recipe to format
 * @returns A formatted recipe
 */
function formatRecipe(recipe: LLMRecipeOutgoing): LLMRecipeOutgoing {
    const formatted = { ...recipe };

    // Format recipe name
    if (formatted.name) {
        formatted.name = toTitleCase(formatted.name);
    }

    // Format ingredient names
    if (formatted.ingredients) {
        formatted.ingredients = formatted.ingredients.map(formatIngredient);
    }

    return formatted;
}

/**
 * Formats all name-related properties in an ingredient/search result
 * @param ingredient The ingredient to format
 * @returns A formatted ingredient
 */
function formatIngredient(ingredient: OutgoingSearchResult): OutgoingSearchResult {
    const formatted = { ...ingredient };

    // Format all name-related properties
    if (formatted.displayName) {
        formatted.displayName = toTitleCase(formatted.displayName);
    }
    
    // if (formatted.stemmedDisplayName) {
    //     formatted.stemmedDisplayName = toTitleCase(formatted.stemmedDisplayName);
    // }
    
    // if (formatted.shortName) {
    //     formatted.shortName = toTitleCase(formatted.shortName);
    // }
    
    // if (formatted.longName) {
    //     formatted.longName = toTitleCase(formatted.longName);
    // }
    
    // if (formatted.scoredName) {
    //     formatted.scoredName = toTitleCase(formatted.scoredName);
    // }
    
    if (formatted.brandName) {
        formatted.brandName = toTitleCase(formatted.brandName);
    }
    
    // if (formatted.ingredientName) {
    //     formatted.ingredientName = toTitleCase(formatted.ingredientName);
    // }
    
    if (formatted.mealName) {
        formatted.mealName = toTitleCase(formatted.mealName);
    }
    
    if (formatted.componentName) {
        formatted.componentName = toTitleCase(formatted.componentName);
    }

    return formatted;
}

/**
 * Formats a shopping item's name
 * @param item The shopping item to format
 * @returns A formatted shopping item
 */
function formatShoppingItem(item: ShoppingItem): ShoppingItem {
    return {
        ...item,
        name: toTitleCase(item.name)
    };
}

/**
 * Formats any partial meal plan data structure
 * This function can handle incomplete or nested structures returned from regeneration
 * @param data Any meal plan-related data structure
 * @returns The formatted data structure
 */
export function formatPartialMealPlanData<T>(data: T): T {
    if (!data || typeof data !== 'object') {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(formatPartialMealPlanData) as T;
    }

    const formatted = { ...data } as any;

    // Handle full meal plan structure
    if ('mealPlanDays' in formatted || 'shoppingList' in formatted) {
        return formatMealPlanNames(formatted as LLMMealPlanResultOutgoing) as T;
    }

    // Handle individual recipe
    if ('name' in formatted && 'ingredients' in formatted) {
        return formatRecipe(formatted as LLMRecipeOutgoing) as T;
    }

    // Handle individual ingredient/search result
    if ('displayName' in formatted || 'ingredientName' in formatted) {
        return formatIngredient(formatted as OutgoingSearchResult) as T;
    }

    // Handle shopping item
    if ('name' in formatted && 'portionQuantity' in formatted && 'portionSize' in formatted) {
        return formatShoppingItem(formatted as ShoppingItem) as T;
    }

    // Handle nested objects recursively
    for (const key in formatted) {
        if (formatted.hasOwnProperty(key) && typeof formatted[key] === 'object') {
            formatted[key] = formatPartialMealPlanData(formatted[key]);
        }
    }

    return formatted as T;
} 